import { SupabaseClient } from '@supabase/supabase-js';

import { TABLES } from '../../../config/supabase';
import { AuthError, Goal, UserPreferences } from '../../../types';

/**
 * User profile management service
 */
export class ProfileService {
  private supabase: SupabaseClient;

  constructor(supabaseClient: SupabaseClient) {
    this.supabase = supabaseClient;
  }

  // ============================================================================
  // Profile Management
  // ============================================================================

  async updateUserProfile(
    userId: string,
    updates: Partial<{
      first_name: string;
      last_name: string;
      display_name: string;
      avatar_url: string;
      age_group: string;
      income_range: string;
      employment_status: string;
      dependents: number;
      investment_experience: string;
      risk_tolerance: string;
      investment_timeline_years: number;
      status: string;
      onboarding_completed_at: string;
    }>
  ): Promise<{
    success: boolean;
    profile?: UserPreferences;
    error?: AuthError;
  }> {
    try {
      console.log('🔄 Updating user profile for:', userId, updates);

      const { data, error } = await this.supabase
        .from(TABLES.USER_PROFILES)
        .upsert(
          {
            id: userId,
            ...updates,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        )
        .select()
        .single();

      if (error) {
        console.log('❌ Error updating user profile:', error.message);
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: error.message,
          },
        };
      }

      console.log('✅ User profile updated successfully');
      return {
        success: true,
        profile: data,
      };
    } catch (error) {
      console.log('❌ Unexpected error updating user profile:', error);
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async getUserProfiles(userId: string): Promise<{
    profiles: Goal[];
    error?: AuthError;
  }> {
    try {
      // Import GoalsService dynamically to avoid circular dependency
      const { GoalsService } = await import('../../goals/goalsService');
      const result = await GoalsService.getUserGoalsById(userId);
      return {
        profiles: result.goals,
        error: result.error,
      };
    } catch (error) {
      return {
        profiles: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  // ============================================================================
  // Goals Management
  // ============================================================================

  async checkUserHasGoals(userId: string): Promise<boolean> {
    try {
      const startTime = Date.now();
      try {
        const queryPromise = this.supabase
          .from(TABLES.GOALS)
          .select('id')
          .eq('user_id', userId)
          .limit(1);

        const result = await queryPromise;

        if (result.error) {
          console.error('❌ Query error:', result.error);
          return false;
        }

        const hasGoals = result.data && result.data.length > 0;
        return hasGoals;
      } catch (error) {
        console.error(
          `❌ Query failed after ${Date.now() - startTime}ms:`,
          error
        );
        return false;
      }
    } catch (error) {
      console.error('❌ Unexpected error checking user goals:', error);
      return false;
    }
  }

  // Legacy method for backward compatibility
  async hasGoals(userId: string): Promise<{
    hasGoals: boolean;
    error?: AuthError;
  }> {
    try {
      const hasGoals = await this.checkUserHasGoals(userId);
      return {
        hasGoals,
      };
    } catch (error) {
      return {
        hasGoals: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }
}
